import { addDays, endOfDay, format, getDay, parse, startOfDay, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { dateFnsLocalizer, NavigateAction, Views } from 'react-big-calendar';

export type CalendarView = (typeof Views)[keyof typeof Views];

export const THREE_DAY_VIEW_LENGTH = 3;

export const normalizeHiddenWeekdays = (hiddenWeekdays: readonly number[] = []): number[] => {
  const days = [...new Set(hiddenWeekdays.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))];
  return days.length === 7 ? [] : days;
};

export const getReservationCalendarDays = (view: CalendarView, date: Date, hiddenWeekdays: readonly number[] = []): Date[] => {
  if (view === Views.DAY) return [startOfDay(date)];
  const hiddenDays = normalizeHiddenWeekdays(hiddenWeekdays);
  if (view !== Views.WORK_WEEK) {
    const firstDay = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, dayIndex) => addDays(firstDay, dayIndex)).filter(
      (day) => !hiddenDays.includes(getDay(day)),
    );
  }

  const days: Date[] = [];
  let nextDate = startOfDay(date);
  while (days.length < THREE_DAY_VIEW_LENGTH) {
    if (!hiddenDays.includes(getDay(nextDate))) days.push(nextDate);
    nextDate = addDays(nextDate, 1);
  }
  return days;
};

export const getReservationCalendarRange = (
  view: CalendarView,
  date: Date,
  hiddenWeekdays: readonly number[] = [],
): { start: Date; end: Date } => {
  const days = getReservationCalendarDays(view, date, hiddenWeekdays);
  return { start: startOfDay(days[0]), end: endOfDay(days[days.length - 1]) };
};

export const navigateReservationCalendar = (
  view: CalendarView,
  date: Date,
  action: NavigateAction,
  hiddenWeekdays: readonly number[] = [],
): Date => {
  if (action !== 'PREV' && action !== 'NEXT') return date;
  const direction = action === 'NEXT' ? 1 : -1;
  if (view === Views.DAY) return addDays(date, direction);
  if (view !== Views.WORK_WEEK) return addDays(date, direction * 7);

  const hiddenDays = normalizeHiddenWeekdays(hiddenWeekdays);
  if (!hiddenDays.length) return addDays(date, direction * THREE_DAY_VIEW_LENGTH);

  let nextDate = getReservationCalendarDays(view, date, hiddenDays)[0];
  let remainingDays = THREE_DAY_VIEW_LENGTH;
  while (remainingDays > 0) {
    nextDate = addDays(nextDate, direction);
    if (!hiddenDays.includes(getDay(nextDate))) remainingDays--;
  }
  return nextDate;
};

const locales = { es };

export const calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export const calendarMessages = {
  week: 'Semana',
  work_week: 'Semana de trabajo',
  day: 'Día',
  month: 'Mes',
  previous: 'Anterior',
  next: 'Siguiente',
  today: 'Hoy',
  agenda: 'Agenda',
};
